import React, { FC, useEffect, useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import * as yup from 'yup';
import { useRouter } from 'next/router';
import { useLazyQuery } from '@apollo/client';
import { Button, makeStyles, useMediaQuery } from '@material-ui/core';
import { Link, Typography } from '@care/react-component-lib';
import { ErrorNotification, FormikInlineTextField } from '@care/enterprise-components';
import { theme } from '@care/material-ui-theme';
import { AmplitudeLogger } from '@/utilities/analyticsHelper';
import {
  ENTERPRISE_ROUTES,
  EventNames,
  FEATURE_FLAGS,
  LoginLocation,
  memberEnrollment,
  TEXTS,
} from '@/constants';
import CompanySearchInput, {
  CompanySearchSelection,
} from '@/components/pages/MemberEnrollment/Welcome/components/CompanySearchForm/CompanySearchInput';
import TermsOfUse from '@/components/shared/TermsOfUse';
import useDirectEnrollmentData from '@/components/hooks/useDirectEnrollmentData';
import { DirectEnrollment } from '@/types/directEnrollment';
import {
  verifyEmployeeExistsByEmailVariables,
  verifyEmployeeExistsByEmail,
} from '@/__generated__/verifyEmployeeExistsByEmail';
import { VERIFY_EMPLOYEE_EXISTS_BY_EMAIL } from '@/components/request/GQL';
import OverlaySpinner from '@/components/shared/OverlaySpinner';
import useEnterKey from '@/components/hooks/useEnterKey';
import { useFlag } from '@/context/FeatureFlagsContext';

export interface CompanySearchFormProps {
  logger: AmplitudeLogger;
}

const INPUT_WIDTH = '296px';
const CTA_TEXT = 'Join now';

interface ValidationSchema {
  email: yup.StringSchema<string, object>;
  groupShortName: yup.StringSchema<string, object>;
  groupDisplayName: yup.StringSchema<string, object>;
}

export const validationSchema: yup.ObjectSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  groupShortName: yup.string().required('Employer name is required'),
  groupDisplayName: yup.string().required('Employer name is required'),
} as ValidationSchema);

const CompanySearchForm: FC<CompanySearchFormProps> = ({ logger }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const router = useRouter();
  const { fetch, update } = useDirectEnrollmentData();
  const storageValue = fetch();
  const excludeClients = useFlag(FEATURE_FLAGS.ENT_REDIRECT_LD_FLAG.key);
  const lifeCareTransitionD2EClients = useFlag(
    FEATURE_FLAGS.ENT_LIFECARE_TRANSITION_D2E_CLIENT_FLAG.key
  );
  const [memberExists, setMemberExists] = useState<boolean>(false);
  const [apolloError, setApolloError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [ssoConnectionPath, setSSOConnectionPath] = useState<string | null>('');
  const [verifyEmployeeExists, { data: verifyEmployeeData, error: verifyEmployeeError, loading }] =
    useLazyQuery<verifyEmployeeExistsByEmail, verifyEmployeeExistsByEmailVariables>(
      VERIFY_EMPLOYEE_EXISTS_BY_EMAIL,
      { context: { skipAuth: true } }
    );

  useEffect(() => {
    if (verifyEmployeeData) {
      const { enterpriseVerifyEmployeeExistsByEmail } = verifyEmployeeData;
      if (
        enterpriseVerifyEmployeeExistsByEmail.__typename ===
        'EnterpriseVerifyEmployeeExistsByEmailSuccess'
      ) {
        const alreadyExists = enterpriseVerifyEmployeeExistsByEmail.memberExists;
        if (!alreadyExists) {
          if (ssoConnectionPath) {
            window.location.href = ssoConnectionPath;
            return;
          }
          if (
            Boolean(lifeCareTransitionD2EClients) &&
            storageValue.groupShortName in lifeCareTransitionD2EClients
          ) {
            router.push(
              `${memberEnrollment}${ENTERPRISE_ROUTES.LIFECARE_AUTHENTICATION}?groupShortName=${storageValue.groupShortName}`
            );
            return;
          }
          router.push(`${memberEnrollment}${ENTERPRISE_ROUTES.PERSONAL_INFO}`);
          return;
        }
        if (alreadyExists) {
          logger({
            name: EventNames.ERROR_VIEWED,
            data: {
              message: TEXTS.ERRORS.EMAIL_ALREADY_EXISTS,
              step_name: 'Welcome',
              step_number: '0',
              wps_group: formik.values.groupShortName,
            },
          });
        }
        setMemberExists(enterpriseVerifyEmployeeExistsByEmail.memberExists);
      }
      if (enterpriseVerifyEmployeeExistsByEmail.__typename === 'EnterpriseFailureResponse') {
        setApolloError(enterpriseVerifyEmployeeExistsByEmail.knownErrors[0].message);
      }
      setIsSubmitting(false);
    }
    if (verifyEmployeeError) {
      setIsSubmitting(false);
      setApolloError(TEXTS.ERRORS.UNEXPECTED_ERROR);
    }
  }, [verifyEmployeeData, verifyEmployeeError]);

  const handleFormSubmit = async (values: Partial<DirectEnrollment>) => {
    setIsSubmitting(true);
    const { groupShortName, groupDisplayName, email } = values;
    update({ groupShortName, groupDisplayName, email });

    if (Boolean(excludeClients) && groupShortName! in excludeClients) {
      window.location.href = `${window.location.protocol}//${window.location.host}/${groupShortName}`;
      return;
    }
    if (email && groupShortName) {
      verifyEmployeeExists({
        variables: {
          input: {
            email,
            groupShortName,
          },
        },
      });
    }
  };
  const formik = useFormik<Partial<DirectEnrollment>>({
    initialValues: {
      groupShortName: storageValue.groupShortName,
      groupDisplayName: storageValue.groupDisplayName,
      email: storageValue.email,
    },
    validationSchema,
    validateOnBlur: true,
    onSubmit: handleFormSubmit,
  });

  const handleSelection = async ({ name, shortName }: CompanySearchSelection) => {
    formik.setFieldValue('groupShortName', shortName);
    formik.setFieldValue('groupDisplayName', name);
    if (name.length >= 2) {
      await formik.setFieldTouched('groupShortName', true, true);
      await formik.setFieldTouched('groupDisplayName', true, true);
    }
  };

  const handleSubmit = () => {
    logger({
      name: EventNames.CTA_INTERACTED,
      data: {
        cta_text: CTA_TEXT,
        user_email: formik.values.email,
        wps_group: formik.values.groupShortName,
        lifecare_transition_client: isLifecareTransitionClient().toString(),
        sso_client: Boolean(ssoConnectionPath).toString(),
      },
    });
    formik.submitForm();
  };

  const isLifecareTransitionClient = () => {
    return formik.values.groupShortName
      ? formik.values.groupShortName in lifeCareTransitionD2EClients
      : false;
  };

  const classes = makeStyles(() => ({
    grid: {
      display: 'grid',
      gridTemplateAreas: `
        'form'
        'cta'
      `,
    },
    form: {
      gridArea: 'form',
      display: 'grid',
      position: 'relative',
      minWidth: `calc(${INPUT_WIDTH} * 2 + 16px)`,
      '& .fields': {
        display: 'grid',
        gap: isMobile ? 0 : '16px',
        gridTemplateColumns: isMobile ? '1fr' : `repeat(2, ${INPUT_WIDTH})`,
        gridAutoRows: isMobile ? '1fr' : '100%',
        padding: '0 0 16px',
        paddingRight: '24px',
        '& .MuiTextField-root': {
          padding: 0,
          width: `clamp(${INPUT_WIDTH}, 400px, 100%)`,
        },
      },
    },
    button: {
      gridArea: 'cta',
      display: 'block',
      position: isMobile ? 'sticky' : 'relative',
      bottom: isMobile ? '20px' : 0,
      margin: isMobile ? '24px auto 0' : '24px 0 0',
      minHeight: '56px',
      marginBottom: theme.spacing(4),
      zIndex: 2,
    },
  }))();

  const handleLogin = () => {
    logger({
      name: EventNames.LOGIN,
      data: {
        login_location: LoginLocation.WELCOME_PAGE_EMAIL_ERROR,
      },
    });
    window.location.href = `/vis/auth/login`;
  };

  const handleCompanySearchError = (errorMessage: string): void => {
    formik.setFieldError('groupShortName', errorMessage);
    formik.setFieldTouched('groupShortName', true, false);
  };

  const helpText = memberExists && (
    <Typography color="error" careVariant="info1">
      {TEXTS.ERRORS.EMAIL_ALREADY_EXISTS}
      <Link onClick={handleLogin} careVariant="link4" bold>{` Log in?`}</Link>
    </Typography>
  );

  const companySearchPreFillName =
    (router?.query?.groupName as string) || storageValue?.groupDisplayName || '';

  const shouldAutoFocusEmail = Boolean(companySearchPreFillName) && !storageValue.email;

  useEnterKey(true, handleSubmit);

  return (
    <>
      <OverlaySpinner isTransparent isOpen={isSubmitting} />
      <div className={classes.grid}>
        <div className={classes.form}>
          <FormikProvider value={formik}>
            <ErrorNotification text={apolloError} />
            <div className="fields">
              <CompanySearchInput
                onSelection={handleSelection}
                onError={handleCompanySearchError}
                setSSOConnectionPath={setSSOConnectionPath}
                autoFocus={!shouldAutoFocusEmail}
                prefillName={companySearchPreFillName}
                preFetch={Boolean(router?.query?.groupName)}
              />
              <FormikInlineTextField
                autoFocus={shouldAutoFocusEmail}
                id="email"
                name="email"
                label="Email"
                helperText={helpText}
              />
            </div>
          </FormikProvider>
        </div>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
          className={classes.button}>
          {CTA_TEXT}
        </Button>
        <TermsOfUse amplitudeLogger={logger} buttonCaption={CTA_TEXT} abbreviated flat />
      </div>
    </>
  );
};

export default CompanySearchForm;
